import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './footer.sass';
import { fetchNextPage, fetchPrevPage, postsSelector } from '../../modules/posts';
import { ITheme } from '../../app';
import { getConfig } from '../../../../url-reader';
import { MessageService } from '../../../../message-service';
import { RootState } from '../../redux/reducers';
import { globalConfig } from '../../../../shared/config/config';
import { TintAnalyticsTracker } from '../../../../shared/utils/tracker';
import InfiniteScroll from '../infinite-scroll/infinite-scroll';
import ClickForMore from '../../../../shared/components/click-for-more/click-for-more';
import Pagination from '../pagination/pagination';

declare global {
  interface Window {
    analytics: {
      track: (msg: string, body?: { [key: string]: string }) => void;
    };
  }
}

interface IFooterProps {
  theme: ITheme;
  onNewContent?: () => void;
}

const Footer: FC<IFooterProps> = ({ theme, onNewContent }) => {
  const dispatch = useDispatch();
  const config = getConfig();
  const count = config && config.count ? config.count : globalConfig.defaultQueryCount;
  const isPostsFetched = useSelector(postsSelector).length < count;
  const nextLinksList = useSelector((state: RootState) => state.posts.data.flatMap(e => e.links.next));
  const isPostsFetchedByNextLinks = nextLinksList.includes(undefined);
  const tintAnalyticsTracker = TintAnalyticsTracker(!config.noTrack);

  const {
    config: { paginate: isPagination, clickForMore: isClickForMore, infiniteScroll: isInfiniteScroll, noButtons },
    personalization: {
      attributes: {
        data: { waterfallToggle },
      },
    },
    meta: { id: tintId },
  } = theme;

  const trackLoadNewContent = (): void => {
    tintAnalyticsTracker.track('embed_paginated', {
      tint_id: tintId,
    });
  };

  const scrollEmbedToTop = () => {
    MessageService.send({
      type: globalConfig.postMessage.actions.scrollToTopMessage,
    });
  };

  const onFetchNextPage = (): any => dispatch(fetchNextPage(false));

  const onFetchNextPageWithPagination = (isPagination?: boolean) => {
    dispatch(fetchNextPage(isPagination));
    scrollEmbedToTop();
  };
  const onFetchPrevPageWithPagination = (isPagination?: boolean) => {
    dispatch(fetchPrevPage(isPagination));
    scrollEmbedToTop();
  };

  const renderFooterWithNoButtons = () => {
    return isPagination ? null : isInfiniteScroll && !waterfallToggle ? (
      <InfiniteScroll
        fetchNextPage={onFetchNextPage}
        onNewContent={trackLoadNewContent}
        isPostFetched={isPostsFetched}
      />
    ) : null;
  };

  return noButtons ? (
    renderFooterWithNoButtons()
  ) : (
    <div className="footer" data-testid="footer">
      {isClickForMore ? (
        <ClickForMore
          onNewContent={trackLoadNewContent}
          fetchNextPage={() => {
            onFetchNextPage().then(() => {
              setTimeout(() => onNewContent && onNewContent(), 0);
            });
          }}
        />
      ) : isPagination ? (
        <Pagination
          onNewContent={trackLoadNewContent}
          fetchNextPage={onFetchNextPageWithPagination}
          fetchPrevPage={onFetchPrevPageWithPagination}
          isPostsFetched={isPostsFetched}
        />
      ) : (
        <InfiniteScroll
          fetchNextPage={onFetchNextPage}
          onNewContent={trackLoadNewContent}
          isPostFetched={isPostsFetchedByNextLinks}
        />
      )}
    </div>
  );
};

export default Footer;
